import { useState } from 'react';
import { Bell, ChevronRight } from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { useSuggestions } from '../../context/SuggestionsContext';
import SuggestionCard from './SuggestionCard';

interface SuggestionMenuProps {
  onViewMore: () => void;
}

const SuggestionMenu = ({ onViewMore }: SuggestionMenuProps) => {
  const [open, setOpen] = useState(false);
  const { suggestions, suggestionsCount } = useSuggestions();
  const isMobile = window.innerWidth <= 768;
  const moreSuggestionsToView = suggestionsCount - 5;

  if (suggestionsCount === 0) return null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className={cn(
            'relative flex items-center h-[50px] hover:bg-[#106ebe] transition-colors rounded ml-4 mr-4 notification-text',
            isMobile ? 'w-[50px] justify-center' : 'gap-2 text-sm px-3'
          )}
          aria-label={`${suggestionsCount} notifications`}
        >
          <Bell className="h-4 w-4 flex-shrink-0" />
          {!isMobile && (
            <>
              <span>You have</span>
              <Badge variant="secondary">{suggestionsCount}</Badge>
              <span>notifications</span>
            </>
          )}
          {isMobile && (
            <span className="absolute top-2 right-1 h-4 min-w-4 px-0.5 bg-destructive rounded-full text-[10px] flex items-center justify-center font-bold ">
              {suggestionsCount}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="p-0 mt-2"
        style={{ width: isMobile ? '100vw' : '60vw' }}
      >
        <div className={'max-h-[80vh] overflow-y-auto'}>
          {suggestions.slice(0, 5).map((suggestion: any) => (
            <div
              key={suggestion.Id.toString()}
              className="p-[10px]"
            >
              <SuggestionCard
                suggestion={suggestion}
                size="half"
                refreshParent={() => console.log('Refresh parent called')}
                showDocuSignMessage={false}
                adminMode={false}
                isViewMoreMobile={isMobile}
              />
            </div>
          ))}
          {moreSuggestionsToView > 0 && (
            <div className="p-3">
              <Button
                variant="ghost"
                className="font-semibold text-[#ba56b7] hover:text-[#ba56b7]"
                onClick={() => {
                  setOpen(false);
                  onViewMore();
                }}
              >
                And {moreSuggestionsToView} more notifications
                <ChevronRight className="ml-1 h-3 w-3" />
              </Button>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default SuggestionMenu;
